import { useAuth0 } from '@auth0/auth0-react';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { persistQueryClient } from '@tanstack/query-persist-client-core';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { useEffect, useMemo, useRef } from 'react';
import { wrapAccessTokenAccessor } from './auth/access-token';
import { isDemoMode } from './auth/demo-auth-provider';
import { Loading } from './components/loading';
import { TooltipProvider } from './components/popover/tooltip';
import { AuthError } from './components/route-errors';
import { QUERY_CLIENT_MAX_AGE, QueryClientConfig } from './query/client-config';
import { createIndexedDBPersister } from './query/indexeddb-persister';
import { router } from './router';

// Capture auth error from URL params once at module load, before any renders.
// This avoids reading window.location.search on every render and prevents
// the replaceState side effect from running during the render phase.
const initialSearchParams = new URLSearchParams(window.location.search);
const initialAuthError = initialSearchParams.has('error')
  ? initialSearchParams.get('error_description') ||
    'Authentication was not completed. Please try again.'
  : null;
const hasAuthCallback = initialSearchParams.has('code') && initialSearchParams.has('state');

export function App() {
  const auth = useAuth0();

  const queryClient = useMemo(() => {
    const client = new QueryClient(QueryClientConfig);

    // Set up persistence
    const persister = createIndexedDBPersister();
    persistQueryClient({
      queryClient: client,
      persister,
      maxAge: QUERY_CLIENT_MAX_AGE,
    });

    return client;
  }, []);

  const wrappedAuth = useMemo(
    () => ({
      ...auth,
      getAccessTokenSilently: wrapAccessTokenAccessor(
        auth.getAccessTokenSilently,
        auth.loginWithRedirect
      ),
    }),
    [auth]
  );

  useEffect(() => {
    if (
      import.meta.env.MODE === 'production' &&
      !isDemoMode() &&
      auth.isAuthenticated &&
      auth.user?.sub
    ) {
      const user = { id: auth.user.sub, name: auth.user.name, email: auth.user.email };
      datadogRum.setUser(user);
      datadogLogs.setUser(user);
    }
  }, [auth.isAuthenticated, auth.user]);

  // Strip auth error/callback params from the URL once Auth0 has processed them.
  // This must wait for the callback to complete — Auth0Provider's effect reads
  // window.location.search, and child effects run before parent effects, so an
  // eager replaceState would wipe the params before Auth0 sees them.
  const cleanedUrl = useRef(false);
  useEffect(() => {
    if (cleanedUrl.current) {
      return;
    }

    // Wait for Auth0 to finish processing the callback before stripping params
    if (hasAuthCallback && !auth.isAuthenticated && !auth.error) {
      return;
    }

    if (initialAuthError || hasAuthCallback) {
      window.history.replaceState({}, '', window.location.pathname);
      cleanedUrl.current = true;
    }
  }, [auth.isAuthenticated, auth.error]);

  // Wait for Auth0 to finish processing the OAuth callback before rendering the router.
  // Auth0's handleRedirectCallback runs in a useEffect, so there's a window where
  // isLoading is false but the code exchange hasn't completed yet. If we render the
  // router too early, authenticatedLayout.beforeLoad would see isAuthenticated=false
  // and trigger another login redirect, creating a loop.
  const isAuthProcessing = hasAuthCallback && !auth.isAuthenticated && !auth.error;

  if (auth.isLoading || isAuthProcessing) {
    return <Loading />;
  }

  // If Auth0 returned an error in the URL (e.g., access_denied after consent decline)
  // and the user isn't authenticated, show a login prompt instead of letting the
  // router's auth guard call loginWithRedirect() again (which would bypass the
  // denial via Auth0's cached SSO session).
  if (initialAuthError && !auth.isAuthenticated) {
    return (
      <AuthError
        message={initialAuthError}
        onRetry={() => auth.loginWithRedirect({ authorizationParams: { prompt: 'login' } })}
      />
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={300} skipDelayDuration={150}>
        <RouterProvider router={router} context={{ auth: wrappedAuth, queryClient }} />
      </TooltipProvider>
    </QueryClientProvider>
  );
}
